<template>
  <div>
    <!-- Download by link -->
    <b-card class="mb-2" border-variant="secondary">
      <h6>Скачать по ссылке</h6>
      <b-input-group>
        <b-form-input
            v-model="downloadUrl"
            placeholder="Вставьте ссылку VK или YouTube (например, https://vk.com/audio-2001_123 или https://youtube.com/watch?v=...)"
            :disabled="isDownloadingByUrl"
            @keyup.enter="downloadByUrl"
        />
        <b-input-group-append>
          <b-button
              variant="success"
              :disabled="!downloadUrl || isDownloadingByUrl"
              @click="downloadByUrl"
          >
            <b-spinner v-if="isDownloadingByUrl" small class="mr-50"/>
            <feather-icon v-else icon="DownloadIcon" class="mr-50"/>
            Скачать
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-card>

    <!-- Source selection buttons -->
    <div class="d-flex justify-content-center mb-2">
      <b-button
          :variant="selectedSource === 'vk' ? 'primary' : 'outline-primary'"
          class="mr-1"
          @click="searchBySource('vk')"
      >
        ВКонтакте
      </b-button>
      <b-button
          :variant="selectedSource === 'youtube' ? 'danger' : 'outline-danger'"
          @click="searchBySource('youtube')"
      >
        YouTube
        <feather-icon
            v-if="trackInfo && trackInfo.has_yt_cache"
            v-b-tooltip.hover
            title="Результаты из кеша"
            icon="DatabaseIcon"
            size="14"
            class="ml-25"
        />
      </b-button>
      <b-button
          v-b-tooltip.hover
          title="Сбросить YouTube кеш"
          variant="outline-dark"
          class="ml-1"
          :disabled="!trackInfo || !trackInfo.has_yt_cache"
          @click="clearYoutubeCache"
      >
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
        Сброс YT кеша
      </b-button>
    </div>

    <!-- Search results -->
    <b-row v-if="selectedSource">
      <b-col
          cols="12"
          md="12"
          class="d-flex align-items-center justify-content-center mb-2 mb-md-0"
      >
        <b-card
            title="Поиск песни"
            class="col-md-12 col-12"
        >
          <b-overlay
              :show="isLoadingTrack"
              rounded="sm"
          >
            <b-row>
              <b-col md="12">

                <div class="my-1 py-25">
                  <!-- Search error -->
                  <div v-if="searchError" class="text-center py-2">
                    <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger mb-1"/>
                    <p class="text-danger font-weight-bold">{{ searchError }}</p>
                  </div>

                  <!-- VK results table -->
                  <b-table
                      v-else-if="selectedSource === 'vk'"
                      ref="refTracksListTable"
                      :items="searchResults"
                      responsive
                      :fields="vkTableColumns"
                      :tbody-tr-class="rowClass"
                      primary-key="id"
                      show-empty
                      empty-text="No tracks"
                      class="position-relative"
                  >

                    <!-- Column: Artist -->
                    <template #cell(artist)="data">
                      <b-media vertical-align="center" v-if="data.item.main_artists">
                        <template #aside v-if="data.item.album && data.item.album.thumb">
                          <b-avatar
                              size="64"
                              :src="data.item.album.thumb.photo_270"
                              :text="avatarText(data.item.main_artists[0].name)"
                              variant="primary"
                          />
                        </template>
                        <span class="font-weight-bold d-block text text-success" v-for="(artist, index) in data.item.main_artists">
                          {{ artist.name }}
                          <b-button
                              v-if="trackInfo && artist.name != trackInfo.artist"
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="info"
                              class="btn-icon"
                              size="sm"
                              @click="addAlias(trackInfo.artist_id, artist.name)"
                          >
                            <feather-icon icon="UserPlusIcon"/>
                          </b-button><span v-if="index < data.item.main_artists.length - 1">,</span>
                        </span>
                      </b-media>
                      <span class="text" v-else>
                        {{ data.item.artist }}
                      </span>
                    </template>

                    <!-- Column: Duration -->
                    <template #cell(duration)="data">
                      <span class="text-nowrap">
                        {{ formatDuration(data.item.duration) }}
                      </span>
                    </template>

                    <!-- Column: Title -->
                    <template #cell(title)="data">
                      {{ data.item.title }}
                      <span class="small" v-if="data.item.subtitle">
                        {{ data.item.subtitle }}
                      </span>
                      <b-button
                          v-if="trackInfo && ((data.item.artist != trackInfo.artist) || (data.item.title != trackInfo.track))"
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="warning"
                          class="btn-icon ml-50"
                          size="sm"
                          @click="updateTrackTitle(trackInfo.id, `${data.item.artist} - ${data.item.title}`)"
                      >
                        <feather-icon icon="ArrowUpCircleIcon"/>
                      </b-button>
                    </template>

                    <!-- Column: Date -->
                    <template #cell(date)="data">
                      <span class="text-nowrap">
                        {{ fromTimestamp(data.item.date) }}
                      </span>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)="data">
                      <span class="text-nowrap">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="success"
                            class="btn-icon"
                            @click="downloadTrack(`${data.item.owner_id}_${data.item.id}`)"
                        >
                          <feather-icon icon="DownloadIcon"/>
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="primary"
                            class="btn-icon"
                            target="_blank"
                            :href="`https://vk.com/audio${data.item.owner_id}_${data.item.id}`"
                        >
                          <feather-icon icon="SearchIcon"/>
                        </b-button>
                      </span>
                    </template>
                  </b-table>

                  <!-- YouTube results table -->
                  <b-table
                      v-else-if="selectedSource === 'youtube'"
                      :items="searchResults"
                      responsive
                      :fields="youtubeTableColumns"
                      primary-key="youtube_external_id"
                      show-empty
                      empty-text="No tracks"
                      class="position-relative"
                  >

                    <!-- Column: Preview -->
                    <template #cell(preview)="data">
                      <img
                          v-if="data.item.preview"
                          :src="data.item.preview"
                          class="yt-preview-thumb"
                          @click="openPreview(data.item.preview)"
                      />
                    </template>

                    <!-- Column: Artist -->
                    <template #cell(artist)="data">
                      <span class="font-weight-bold text-success">
                        {{ data.item.artist }}
                      </span>
                    </template>

                    <!-- Column: Title -->
                    <template #cell(title)="data">
                      {{ data.item.title }}
                      <span class="small text-muted d-block" v-if="data.item.track && data.item.track !== data.item.title">
                        {{ data.item.track }}
                      </span>
                    </template>

                    <!-- Column: Channel -->
                    <template #cell(channel)="data">
                      {{ data.item.channel }}
                    </template>

                    <!-- Column: Duration -->
                    <template #cell(duration)="data">
                      <span class="text-nowrap" v-if="data.item.duration">
                        {{ formatDuration(data.item.duration) }}
                      </span>
                    </template>

                    <!-- Column: Date -->
                    <template #cell(release_datetime)="data">
                      <span class="text-nowrap">
                        {{ data.item.release_datetime }}
                      </span>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)="data">
                      <span class="text-nowrap">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="success"
                            class="btn-icon"
                            @click="downloadYoutubeTrack(data.item.youtube_external_id)"
                        >
                          <feather-icon icon="DownloadIcon"/>
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon ml-50"
                            target="_blank"
                            rel="noopener noreferrer"
                            :href="data.item.url || `https://www.youtube.com/watch?v=${data.item.youtube_external_id}`"
                        >
                          <feather-icon icon="ExternalLinkIcon"/>
                        </b-button>
                      </span>
                    </template>
                  </b-table>
                </div>
              </b-col>
            </b-row>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>

    <!-- Preview modal -->
    <div
        v-if="previewUrl"
        class="yt-preview-overlay"
        @click="closePreview"
    >
      <img :src="previewUrl" class="yt-preview-full"/>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BMedia,
  BAvatar,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BOverlay,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {avatarText, formatDuration, fromTimestamp} from '@core/utils/filter'
import router from "@/router"
import store from "@/store"
import {ref} from "@vue/composition-api"
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BFormInput,
    BMedia,
    BAvatar,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BOverlay,
    BTable,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    trackInfo: {
      type: Object,
      default: null,
    },
  },
  methods: {
    rowClass(item, type) {
      const colorClass = 'table-danger'
      if (!item || type !== 'row') {
        return
      }

      // eslint-disable-next-line consistent-return
      if (false) {
        return colorClass
      }
    },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const searchResults = ref([])
    const selectedSource = ref(null)
    const isLoadingTrack = ref(false)
    const previewUrl = ref(null)
    const searchError = ref(null)
    const downloadUrl = ref('')
    const isDownloadingByUrl = ref(false)

    // VK Table Columns
    const vkTableColumns = [
      {key: 'artist', sortable: true},
      {key: 'title', sortable: true},
      {key: 'duration', sortable: true},
      {key: 'is_licensed', sortable: true},
      {key: 'date', sortable: true},
      {key: 'actions'},
    ]

    // YouTube Table Columns
    const youtubeTableColumns = [
      {key: 'preview', label: 'Превью', sortable: false},
      {key: 'artist', label: 'Артист', sortable: true},
      {key: 'title', label: 'Название', sortable: true},
      {key: 'channel', label: 'Канал', sortable: true},
      {key: 'duration', label: 'Длительность', sortable: true},
      {key: 'release_datetime', label: 'Дата', sortable: true},
      {key: 'actions', label: 'Действия'},
    ]

    const searchBySource = async (source) => {
      selectedSource.value = source
      searchError.value = null
      isLoadingTrack.value = true
      try {
        let id = router.currentRoute.params.id
        const res = await store.dispatch('artists/searchTrack', {id: id, queryParams: {source}})
        searchResults.value = res.data.results || []
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Ошибка поиска'
        searchError.value = message
        searchResults.value = []
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const downloadTrack = async (vkId) => {
      isLoadingTrack.value = true
      let id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/downloadTrack', {id: id, queryParams: {source: 'vk', vkId: vkId}})
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const clearYoutubeCache = async () => {
      const id = router.currentRoute.params.id
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/clearYoutubeCache', {id})
        toast({
          component: ToastificationContent,
          props: {
            title: 'YouTube кеш сброшен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        emit('track-updated')
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сброса YouTube кеша',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const downloadYoutubeTrack = async (youtubeId) => {
      isLoadingTrack.value = true
      let id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/downloadTrackYoutube', {id: id, queryParams: {source: 'youtube', youtubeId: youtubeId}})
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки трека с YouTube',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const parseDownloadUrl = (url) => {
      // VK: https://vk.com/audio-2001_123456 or https://vk.com/audio123_456
      const vkMatch = url.match(/vk\.com\/audio(-?\d+_\d+)/)
      if (vkMatch) {
        return { source: 'vk', id: vkMatch[1] }
      }

      // YouTube: https://www.youtube.com/watch?v=ID or https://youtu.be/ID
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
      if (ytMatch) {
        return { source: 'youtube', id: ytMatch[1] }
      }

      return null
    }

    const downloadByUrl = async () => {
      const url = downloadUrl.value.trim()
      if (!url) return

      const parsed = parseDownloadUrl(url)
      if (!parsed) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось определить тип ссылки. Поддерживаются ссылки VK и YouTube.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      isDownloadingByUrl.value = true
      try {
        if (parsed.source === 'vk') {
          await downloadTrack(parsed.id)
        } else {
          await downloadYoutubeTrack(parsed.id)
        }
        downloadUrl.value = ''
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек успешно загружен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        // ошибки обрабатываются внутри downloadTrack/downloadYoutubeTrack
      } finally {
        isDownloadingByUrl.value = false
      }
    }

    const updateTrackTitle = async (id, title) => {
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/updateTrack', {id: id, queryParams: {title: title}})
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating track title',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const addAlias = async (artistId, alias) => {
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/addAlias', {id: artistId, queryParams: {alias: alias}})
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления алиаса',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const openPreview = (url) => {
      previewUrl.value = url
    }

    const closePreview = () => {
      previewUrl.value = null
    }

    return {
      searchResults,
      selectedSource,
      isLoadingTrack,
      searchError,
      downloadUrl,
      isDownloadingByUrl,
      downloadByUrl,
      searchBySource,
      downloadTrack,
      clearYoutubeCache,
      downloadYoutubeTrack,
      updateTrackTitle,
      addAlias,
      vkTableColumns,
      youtubeTableColumns,
      avatarText,
      formatDuration,
      fromTimestamp,
      previewUrl,
      openPreview,
      closePreview,
    }
  },
}
</script>

<style lang="scss">
.yt-preview-thumb {
  width: 160px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

.yt-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
}

.yt-preview-full {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
